import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useModalActions } from '../../Modals/modalActions';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import { actionCreators as sessionActionCreators } from '@cube3/state/src/redux/ducks/session';
import { VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { useCurrentUser } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { compose } from '../../../../../utils/component-helpers';
import {
  getFormValues,
  reduxForm,
  InjectedFormProps,
  getFormError,
  clearSubmitErrors
} from 'redux-form';
import { useDispatch } from 'react-redux';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import { addFormId } from '@cube3/state/src/redux/middleware/redux-form-middleware';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { actionCreators } from '@cube3/state/src/redux/ducks/request-status';
import { getErrorMessage } from '@cube3/forms/src/utils/getErrorMessage';
import { Image } from '@cube3/cubicle/src/core/atoms/Image';
import { makeCSS } from '@cube3/cubicle/src/utils/makeCSS';
import { useGlobalSubmit } from '@cube3/cubicle/src/helpers/hooks/useGlobalSubmit';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';
import { GenericReduxForm } from '@cube3/forms/src/forms/GenericReduxForm';
import { isMobile } from 'react-device-detect';

const useCSS = makeCSS(({ theme }) => ({
  image: {
    width: theme.spacing[64], //256,
    height: theme.spacing[64], //256,
    borderRadius: theme.spacing[2],
    border: `1px solid ${theme.color.line['03']}`,
    background: theme.color.overlay['01']
  }
}));

interface Props extends InjectedFormProps {}

const AddOrEditAuthenticatorBase = (props: Props) => {
  const { submitSucceeded, submitting, invalid, form } = props;

  const user = useCurrentUser()[1];
  const otpEnabled = user?.otp_enabled;
  const { closeAllModals } = useModalActions();
  const stored = useTypedSelector((state) => state.session);
  const qrCode = stored?.qrCode;

  const formValues = useTypedSelector((state) => getFormValues(form)(state));
  const otpCode = formValues?.['totp_code'];
  const submitErrors = useTypedSelector((state) => getFormError(form)(state));

  const errorMessage = submitErrors && getErrorMessage(submitErrors[0]);

  const dispatch = useDispatch();

  const handleSubmit = useCallback(() => {
    dispatch(addFormId(form)(sessionActionCreators.enrollTOTPFinish(otpCode)));
  }, [form, user, otpCode]);

  useEffect(() => {
    if (submitSucceeded && user) {
      // invalidate cache
      dispatch(
        actionCreators.invalidateResource({ type: 'user', id: user.id })
      );
      closeAllModals();
    }
  }, [submitSucceeded]);

  // clear previous QR code on mount
  useLayoutEffect(() => {
    dispatch(sessionActionCreators.clearEnrollCredentials());
  }, []);

  useGlobalSubmit(handleSubmit, invalid);

  const fields = useMemo(() => {
    return [
      {
        type: 'authentication',
        name: 'totp_code',
        autoFocus: true,
        label: 'Verify the code from the app',
        submitError: errorMessage,
        shouldClear: !!submitErrors // to clear the `AuthCode` input
      } as Field<string, 'authentication'>
    ];
  }, [submitErrors, errorMessage]);

  const disableVerify =
    !otpCode || submitting || invalid || !!submitErrors || otpCode.length !== 6;

  const classes = useCSS();
  return (
    <ModalMenuUI
      multiplyContentSpace={7}
      onCloseEvent={closeAllModals}
      title={`${otpEnabled ? 'Edit' : 'Add'} authenticator app`}
      footerRightComponent={
        <Button
          label="Save"
          onClick={handleSubmit}
          buttonStyle="solid"
          background="primary"
          disabled={disableVerify}
          size={isMobile ? SizeVariants.lg : SizeVariants.md}
        />
      }
      footerLeftComponent={
        <Button
          label="Cancel"
          onClick={closeAllModals}
          buttonStyle="ghost"
          size={isMobile ? SizeVariants.lg : SizeVariants.md}
        />
      }
      loading={submitting}
    >
      <VBox
        gap={16}
        style={{
          padding: '8px 0px',
          marginLeft: isMobile ? '' : '-8px'
        }}
      >
        {!submitSucceeded && qrCode && (
          <>
            <Typography colorVariant="03" variant={SizeVariants.md}>
              Use an authenticator app or browser extension to scan.
            </Typography>

            <Image src={qrCode} alt={'QR code'} className={classes.image} />

            <GenericReduxForm fields={fields} direction="vertical" />
          </>
        )}
      </VBox>
    </ModalMenuUI>
  );
};

export const AddOrEditAuthenticatorModal = compose(AddOrEditAuthenticatorBase)(
  reduxForm({
    form: 'add_authenticator',
    destroyOnUnmount: true,
    onChange: (values, dispatch, props) => {
      // clear submit errors when changing input after submit failed
      if (values?.['totp_code'] && props.submitFailed) {
        dispatch(clearSubmitErrors(props.form));
      }
    }
  })
);
